import { initializeApp } from 'firebase/app'
import {
  GoogleAuthProvider,
  getAuth,
  onAuthStateChanged,
  signInWithPopup,
} from 'firebase/auth'
import {
  type DocumentData,
  doc,
  getDoc,
  getFirestore,
  increment,
  setDoc,
} from 'firebase/firestore/lite'

import { parseChannels, parsePlaylists } from './util'

import type { PlaylistConfig } from './util'
import type { User } from 'firebase/auth'

const app = initializeApp({
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
})

const auth = getAuth(app)
const db = getFirestore(app)
const googleProvider = new GoogleAuthProvider()

interface UserDoc {
  playlists: Record<string, PlaylistConfig>
  channels: Record<string, string>
}

function parseNumberRecord(raw: DocumentData): Record<string, number> {
  return Object.fromEntries(
    Object.entries(raw).filter(
      (e): e is [string, number] => typeof e[1] === 'number',
    ),
  )
}

export function subscribeToAuthChanges(callback: (user: User | null) => void) {
  return onAuthStateChanged(auth, callback)
}

export async function signInWithGoogle() {
  await signInWithPopup(auth, googleProvider)
}

export async function signOut() {
  await auth.signOut()
}

export async function loadUserData(uid: string): Promise<UserDoc | null> {
  const snap = await getDoc(doc(db, 'users', uid))
  const raw = snap.exists() ? snap.data() : null
  return raw
    ? {
        channels: parseChannels(raw.channels),
        playlists: parsePlaylists(raw.playlists),
      }
    : null
}

export async function saveUserData(uid: string, data: UserDoc) {
  await setDoc(doc(db, 'users', uid), data)
}

export async function loadPlayCounts(
  uid: string,
): Promise<Record<string, number>> {
  const snap = await getDoc(doc(db, 'userPlayCounts', uid))
  return snap.exists() ? parseNumberRecord(snap.data()) : {}
}

export async function incrementPlayCount(uid: string, videoId: string) {
  await setDoc(
    doc(db, 'userPlayCounts', uid),
    { [videoId]: increment(1) },
    { merge: true },
  )
}
